import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  inject
} from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { Subscription, finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

import { ServicenumberDetailComponent } from '../detail/servicenumber-detail.component';

@Component({
  selector: 'app-servicenumber-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NzTableModule,
    NzSpinModule,
    NzPaginationModule,
    ReactiveFormsModule,
    NzFormModule,
    FormsModule,
    NzSelectModule,
    NzModalModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzToolTipModule,
    NzEmptyModule,
    NzTagModule,
    NzDropDownModule,
    NzAffixModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './servicenumber-list.component.html',
  styleUrls: ['./servicenumber-list.component.less']
})
export class ServicenumberListComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() tenantId: string = '';
  @Input() tenantType: string = ''; // 新增租戶類型輸入
  @Input() pageTitleKey = 'menu.servicenumber-management';
  @Input() embeddedMode = false;
  @Input() viewMode: 'table' | 'card' = 'table';
  checked = false;
  indeterminate = false;
  setOfCheckedId = new Set<string>();
  listOfCurrentPageData: readonly any[] = [];

  private readonly nzModalData = inject(NZ_MODAL_DATA, { optional: true }) || {};

  selectedServiceNumberMap = new Map<string, { id: string; name: string }>();
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly message = inject(NzMessageService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly modal = inject(NzModalService);
  private readonly imageService = inject(ImageService);
  private readonly ngZone = inject(NgZone);
  @ViewChild('pageScroll') pageScroll?: ElementRef<HTMLElement>;
  @ViewChild('cardLoadMoreAnchor') cardLoadMoreAnchor?: ElementRef<HTMLDivElement>;
  listOfData: any[] = [];
  loading = false;
  // 分页参数
  pageIndex = 1;
  pageSize = 30;
  total = 0;
  hasNextPage = false;
  searchKeywords = '';
  // 筛选参数
  statusFilter: string | null = null;
  openTypeFilter: string | null = null;
  typeFilter: string | null = null;
  robotStatusFilter: string | null = null;
  channelFilter: string | null = null;
  direction = 'desc';
  refreshTime = Date.now();
  showAdvancedFilters = true;
  loadingMore = false;
  isMobileView = false;
  @ViewChildren('descriptionEl') descriptionElements!: QueryList<ElementRef<HTMLElement>>;
  private descriptionElementsSub?: Subscription;
  private overflowRefreshTimer?: ReturnType<typeof setTimeout>;
  private readonly expandedDescriptionKeys = new Set<string>();
  private readonly overflowDescriptionKeys = new Set<string>();
  private cardLoadObserver?: IntersectionObserver;

  openTypeOptions = [
    { text: this.i18n.fanyi('servicenumber.open-type.internal'), value: 'Internal' },
    { text: this.i18n.fanyi('servicenumber.open-type.external'), value: 'External' }
  ];
  typeOptions = [
    { text: this.i18n.fanyi('servicenumber.type.official'), value: 'Official' },
    { text: this.i18n.fanyi('servicenumber.type.manage'), value: 'Manage' },
    { text: this.i18n.fanyi('servicenumber.type.professional'), value: 'Professional' },
    { text: this.i18n.fanyi('servicenumber.type.boss'), value: 'Boss' }
  ];
  statusOptions = [
    { text: this.i18n.fanyi('servicenumber.status.new'), value: 'New' },
    { text: this.i18n.fanyi('servicenumber.status.enable'), value: 'Enable' },
    { text: this.i18n.fanyi('servicenumber.status.disable'), value: 'Disable' }
  ];
  robotStatusOptions = [
    { text: this.i18n.fanyi('servicenumber.card.robot-status.all'), value: null },
    { text: this.i18n.fanyi('servicenumber.card.robot-status.enabled'), value: 'enabled' },
    { text: this.i18n.fanyi('servicenumber.card.robot-status.disabled'), value: 'disabled' }
  ];
  channelOptions = [
    { text: this.i18n.fanyi('servicenumber.card.channel.all'), value: null },
    { text: 'LINE', value: 'LINE' },
    { text: 'Facebook', value: 'Facebook' },
    { text: 'Instagram', value: 'Instagram' },
    { text: 'WebChat', value: 'WebChat' }
  ];

  // TestId properties for template binding
  'servicenumber' = 'servicenumber';
  'list' = 'list';
  'pagination' = 'pagination';

  columns = [
    { title: this.i18n.fanyi('servicenumber.name'), key: 'name' },
    {
      title: this.i18n.fanyi('servicenumber.open-type'),
      key: 'openType',
      filterMultiple: false,
      filterOptions: this.openTypeOptions
    },
    {
      title: this.i18n.fanyi('servicenumber.type'),
      key: 'type',
      filterMultiple: false,
      filterOptions: this.typeOptions
    },
    {
      title: this.i18n.fanyi('servicenumber.status'),
      key: 'status',
      filterMultiple: false,
      filterOptions: this.statusOptions
    },
    { title: this.i18n.fanyi('servicenumber.description'), key: 'description' }
  ];

  constructor() {}
  ngOnInit(): void {
    if (this.nzModalData.tenantId) {
      this.tenantId = this.nzModalData.tenantId;
    }
    this.updateViewportMode();
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.descriptionElementsSub = this.descriptionElements.changes.subscribe(() => {
      this.scheduleRefreshDescriptionOverflow();
    });
    this.scheduleRefreshDescriptionOverflow();
    this.syncCardLoadObserver();
  }

  ngOnDestroy(): void {
    this.descriptionElementsSub?.unsubscribe();
    this.cardLoadObserver?.disconnect();
    if (this.overflowRefreshTimer) {
      clearTimeout(this.overflowRefreshTimer);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportMode(true);
    this.scheduleRefreshDescriptionOverflow();
  }
  get isSelector(): boolean {
    return this.nzModalData.isSelector || false;
  }
  @HostBinding('class.selector-mode')
  get isSelectorMode(): boolean {
    return this.isSelector;
  }

  @HostBinding('class.embedded-mode')
  get isEmbeddedMode(): boolean {
    return this.embeddedMode;
  }

  get isCardMode(): boolean {
    return !this.isSelector && (this.viewMode === 'card' || this.isMobileView);
  }

  get visibleServiceNumbers(): any[] {
    return this.listOfData.filter(item => {
      if (this.robotStatusFilter === 'enabled' && !this.isRobotEnabled(item)) return false;
      if (this.robotStatusFilter === 'disabled' && this.isRobotEnabled(item)) return false;
      if (this.channelFilter && !this.getChannelTypes(item).includes(this.channelFilter)) return false;
      return true;
    });
  }

  get selectedIds(): string[] {
    return this.nzModalData.selectedIds || [];
  }
  // 处理筛选变化
  onFilterChange(key: string, value: string | null): void {
    if (key === 'openType') {
      this.openTypeFilter = value;
    } else if (key === 'type') {
      this.typeFilter = value;
    } else if (key === 'status') {
      this.statusFilter = value;
    }

    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData(true, false);
  }

  // 清除所有筛选
  clearAllFilters(): void {
    this.statusFilter = null;
    this.openTypeFilter = null;
    this.typeFilter = null;
    this.robotStatusFilter = null;
    this.channelFilter = null;
    this.searchKeywords = '';

    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData(true, false);
  }

  // 清除单个筛选
  clearFilter(key: string): void {
    if (key === 'status') {
      this.statusFilter = null;
    } else if (key === 'openType') {
      this.openTypeFilter = null;
    } else if (key === 'type') {
      this.typeFilter = null;
    } else if (key === 'keywords') {
      this.searchKeywords = '';
    } else if (key === 'robotStatus') {
      this.robotStatusFilter = null;
    } else if (key === 'channel') {
      this.channelFilter = null;
    }

    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData(true, false);
  }

  // 获取服务号类型标签
  getServiceNumberTypeLabel(type: string): string {
    const typeMap: { [key: string]: string } = {
      Official: this.i18n.fanyi('servicenumber.type.official'),
      Manage: this.i18n.fanyi('servicenumber.type.manage'),
      Professional: this.i18n.fanyi('servicenumber.type.professional'),
      Normal: this.i18n.fanyi('servicenumber.type.normal'),
      Boss: this.i18n.fanyi('servicenumber.type.boss')
    };
    return typeMap[type] || type;
  }

  // 获取服务号公开类型标签
  getOpenTypeLabel(openType: string): string {
    const openTypeMap: { [key: string]: string } = {
      Internal: this.i18n.fanyi('servicenumber.open-type.internal'),
      External: this.i18n.fanyi('servicenumber.open-type.external')
    };
    return openTypeMap[openType] || openType;
  }

  // 获取服务号公开类型颜色
  getOpenTypeColor(openType: string): string {
    const colorMap: { [key: string]: string } = {
      Internal: 'cyan',
      External: 'geekblue'
    };
    return colorMap[openType] ?? 'default';
  }

  // 获取服务号状态标签
  getStatusLabel(status: string): string {
    const statusMap: { [key: string]: string } = {
      New: this.i18n.fanyi('servicenumber.status.new'),
      Enable: this.i18n.fanyi('servicenumber.status.enable'),
      Disable: this.i18n.fanyi('servicenumber.status.disable')
    };
    return statusMap[status] || status;
  }

  // 获取服务号状态颜色
  getStatusColor(status: string): string {
    const colorMap: { [key: string]: string } = {
      New: 'default',
      Enable: 'success',
      Disable: 'error'
    };
    return colorMap[status] ?? 'default';
  }

  // 获取服务号类型颜色
  getServiceNumberTypeColor(type: string): string {
    const colorMap: { [key: string]: string } = {
      Official: 'red',
      Manage: 'blue',
      Professional: 'purple',
      Normal: 'green',
      Boss: 'orange'
    };
    return colorMap[type] ?? 'default';
  }
  search(): void {
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData(true, false);
  }

  resetSearch(): void {
    this.searchKeywords = '';
    this.refreshTime = Date.now();
    this.pageIndex = 1;
    this.loadData(true, false);
  }

  nzPageIndexChange(index: number): void {
    this.pageIndex = index;
    this.loadData(true, false);
  }

  nzPageSizeChange(size: number): void {
    this.pageSize = size;
    this.refreshTime = Date.now();
    this.pageIndex = 1;
    this.loadData(true, false);
  }

  loadData(includeRefreshTime: boolean = true, append: boolean = false): void {
    if (append) {
      this.loadingMore = true;
    } else {
      this.loading = true;
    }
    const payload: any = {
      tenantId: this.tenantId,
      pageIndex: this.pageIndex - 1,
      pageSize: this.pageSize,
      direction: this.direction,
      key: this.searchKeywords,
      status: this.statusFilter,
      openType: this.openTypeFilter,
      type: this.typeFilter,
      robotStatus: this.robotStatusFilter,
      channel: this.channelFilter
    };
    if (includeRefreshTime && this.refreshTime) {
      payload.refreshTime = this.refreshTime;
    }
    const apiPath = ConstantUtil.ADMIN_SERVICENUMBER_V2_LIST;
    this.http
      .post(apiPath, payload)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.loadingMore = false;
          this.cdr.detectChanges();
          this.scheduleRefreshDescriptionOverflow();
          this.syncCardLoadObserver();
        })
      )
      .subscribe(
        res => {
          if (res?.success) {
            const items = res.data?.items ?? [];
            this.hasNextPage = !!res.data?.hasNextPage;
            if (append && this.isCardMode) {
              const existingIds = new Set(this.listOfData.map(item => item.id));
              const merged = [...this.listOfData];
              for (const item of items) {
                if (!existingIds.has(item.id)) {
                  merged.push(item);
                }
              }
              this.listOfData = merged;
            } else {
              this.listOfData = items;
            }
            this.total = res.data?.totalCount ?? this.listOfData.length;
            this.loadCardAvatarImages(this.listOfData);

            if (this.isSelector && this.selectedIds.length > 0) {
              // 遍历当前页数据，检查是否在预选列表中
              this.listOfData.forEach(item => {
                if (this.selectedIds.includes(item.id)) {
                  // 将预选的服务号添加到选中集合中
                  this.selectedServiceNumberMap.set(item.id, {
                    id: item.id,
                    name: item.name
                  });
                }
              });
            }
          }
        },
        () => {}
      );
  }

  onCardExtraFilterChange(): void {
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData(true, false);
  }

  onCardScroll(event: Event): void {
    if (!this.isMobileView || !this.isCardMode || this.loading || this.loadingMore || !this.hasNextPage) return;
    const target = event.target as HTMLElement;
    const threshold = 120;
    const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
    if (!reachedBottom) return;
    this.loadMoreOnCard();
  }

  loadMoreOnCard(): void {
    if (!this.isMobileView || !this.isCardMode || this.loading || this.loadingMore || !this.hasNextPage) {
      return;
    }
    this.pageIndex += 1;
    this.loadData(false, true);
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  private updateViewportMode(shouldReload = false): void {
    const nextMobile = typeof window !== 'undefined' && window.innerWidth <= 767;
    const modeChanged = this.isMobileView !== nextMobile;
    this.isMobileView = nextMobile;

    if (!modeChanged) {
      return;
    }

    if (this.isMobileView && this.showAdvancedFilters) {
      this.showAdvancedFilters = false;
    }

    if (shouldReload) {
      this.pageIndex = 1;
      this.refreshTime = Date.now();
      this.loadData(true, false);
    } else {
      this.syncCardLoadObserver();
    }
  }

  getAvatarText(name?: string): string {
    if (!name) return 'SN';
    return name.trim().slice(0, 2);
  }

  getCardAvatarUrl(item: any): string {
    return item?.avatarUrl || item?.avatar || item?.imageUrl || item?.headImageUrl || item?.iconUrl || '';
  }

  getCardDescription(item: any): string {
    return item?.description || this.i18n.fanyi('servicenumber.card.no-description');
  }

  shouldShowDescriptionToggle(item: any, index?: number): boolean {
    return this.overflowDescriptionKeys.has(this.getDescriptionKey(item, index));
  }

  isDescriptionExpanded(item: any, index: number): boolean {
    return this.expandedDescriptionKeys.has(this.getDescriptionKey(item, index));
  }

  toggleDescription(item: any, index: number): void {
    const key = this.getDescriptionKey(item, index);
    if (this.expandedDescriptionKeys.has(key)) {
      this.expandedDescriptionKeys.delete(key);
      return;
    }
    this.expandedDescriptionKeys.add(key);
  }

  getDescriptionKey(item: any, index?: number): string {
    if (item?.id) return String(item.id);
    if (typeof index === 'number') return `idx-${index}`;
    return `name-${String(item?.name || '')}-${String(item?.description || '')}`;
  }

  private scheduleRefreshDescriptionOverflow(): void {
    if (this.overflowRefreshTimer) {
      clearTimeout(this.overflowRefreshTimer);
    }
    this.overflowRefreshTimer = setTimeout(() => {
      this.refreshDescriptionOverflow();
    }, 0);
  }

  private refreshDescriptionOverflow(): void {
    if (!this.descriptionElements) return;

    const presentKeys = new Set<string>();
    this.descriptionElements.forEach(elRef => {
      const el = elRef.nativeElement;
      const key = el.getAttribute('data-desc-key') || '';
      if (!key) return;
      presentKeys.add(key);

      // Measure overflow in collapsed state, independent of current expanded state.
      const wasExpanded = el.classList.contains('is-expanded');
      if (wasExpanded) el.classList.remove('is-expanded');
      const hasOverflow = el.scrollHeight > el.clientHeight + 1;
      if (wasExpanded) el.classList.add('is-expanded');

      if (hasOverflow) {
        this.overflowDescriptionKeys.add(key);
      } else {
        this.overflowDescriptionKeys.delete(key);
        this.expandedDescriptionKeys.delete(key);
      }
    });

    [...this.overflowDescriptionKeys].forEach(key => {
      if (!presentKeys.has(key)) this.overflowDescriptionKeys.delete(key);
    });
    [...this.expandedDescriptionKeys].forEach(key => {
      if (!presentKeys.has(key)) this.expandedDescriptionKeys.delete(key);
    });

    this.cdr.markForCheck();
  }

  private syncCardLoadObserver(): void {
    this.cardLoadObserver?.disconnect();

    if (!this.isMobileView || !this.isCardMode || !this.hasNextPage || !this.cardLoadMoreAnchor?.nativeElement) {
      return;
    }

    this.cardLoadObserver = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.ngZone.run(() => this.loadMoreOnCard());
        }
      },
      {
        root: this.embeddedMode ? null : (this.pageScroll?.nativeElement ?? null),
        rootMargin: '0px 0px 220px 0px',
        threshold: 0.01
      }
    );

    this.cardLoadObserver.observe(this.cardLoadMoreAnchor.nativeElement);
  }

  private loadCardAvatarImages(items: any[]): void {
    for (const item of items) {
      if (!item?.avatarId || this.getCardAvatarUrl(item)) {
        continue;
      }
      this.imageService.loadImage(item.avatarId, 'M').subscribe(imageUrl => {
        if (!imageUrl) {
          return;
        }
        item.avatarUrl = imageUrl;
        this.cdr.detectChanges();
      });
    }
  }

  isRobotEnabled(item: any): boolean {
    if (typeof item?.robotEnabled === 'boolean') return item.robotEnabled;
    const robotStatus = String(item?.robotStatus || '').toLowerCase();
    return robotStatus === 'enable' || robotStatus === 'enabled' || robotStatus === 'on' || robotStatus === 'active';
  }

  getChannelTypes(item: any): string[] {
    if (Array.isArray(item?.channelTypes)) {
      return item.channelTypes.filter(Boolean);
    }
    if (Array.isArray(item?.channels)) {
      return item.channels.map((ch: any) => (typeof ch === 'string' ? ch : ch?.type)).filter(Boolean);
    }
    return [];
  }

  getChannelIcon(channelType: string): string {
    const map: Record<string, string> = {
      LINE: 'assets/images/channels/icon-im-line.svg',
      Facebook: 'assets/images/channels/icon-im-fb.svg',
      Instagram: 'assets/images/channels/icon-im-ig.svg',
      WebChat: 'assets/images/channels/webchat.jpg'
    };
    return map[channelType] || 'assets/images/channels/icon-im-line.svg';
  }

  getTypeTagClass(type: string): string {
    const cls: Record<string, string> = {
      Official: 'type-official',
      Manage: 'type-manage',
      Professional: 'type-professional',
      Normal: 'type-normal',
      Boss: 'type-boss'
    };
    return cls[type] || 'type-normal';
  }

  openServiceNumberDetail(item: any): void {
    this.editServiceNumber(item);
  }

  canDeleteServiceNumber(item: any): boolean {
    return item?.type !== 'Official' && item?.type !== 'Manage';
  }

  onCardMenuClick(event: Event): void {
    event.stopPropagation();
  }

  onCardActionClick(event: Event, action: () => void): void {
    event.stopPropagation();
    action();
  }

  toggleDescriptionFromEvent(event: Event, item: any, index: number): void {
    event.stopPropagation();
    this.toggleDescription(item, index);
  }

  deleteServiceNumber(data: any, event?: Event): void {
    event?.stopPropagation();
    if (!this.canDeleteServiceNumber(data)) {
      return;
    }
    this.message.warning(this.i18n.fanyi('servicenumber.delete-not-supported'));
  }

  private getScopedDetailCommands(serviceNumberId?: string): any[] {
    if (this.tenantId) {
      const commands = ['/tenant/service-number-management', this.tenantId, 'detail'];
      if (serviceNumberId) {
        commands.push(serviceNumberId);
      }
      return commands;
    }
    return serviceNumberId ? ['/servicenumber/detail', serviceNumberId] : ['/servicenumber/detail'];
  }

  // 添加服务号
  addServiceNumber(): void {
    // 檢查是否為個人租戶
    if (this.tenantType === 'Person') {
      this.message.warning(this.i18n.fanyi('servicenumber.person-tenant-cannot-create'));
      return;
    }

    const returnUrl = this.buildReturnUrl();

    this.modal.create({
      nzContent: ServicenumberDetailComponent,
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: false,
      nzWidth: this.isMobileView ? 'calc(100vw - 24px)' : '800px',
      nzStyle: { top: this.isMobileView ? '12px' : '24px' },
      nzBodyStyle: {
        padding: '0',
        maxHeight: this.isMobileView ? 'calc(100vh - 24px)' : 'calc(100vh - 48px)',
        overflow: 'auto'
      },
      nzData: {
        createMode: true,
        tenantId: this.tenantId,
        tenantType: this.tenantType,
        returnUrl
      }
    });
  }

  // 檢查是否可以新建服務號
  canAddServiceNumber(): boolean {
    return this.tenantType !== 'Person';
  }

  editServiceNumber(data: any): void {
    this.router.navigate(this.getScopedDetailCommands(data.id), {
      queryParams: {
        tenantType: this.tenantType,
        returnUrl: this.buildReturnUrl()
      }
    });
  }

  private buildReturnUrl(): string {
    const current = this.router.url || '';
    if (!current.startsWith('/')) return current;
    try {
      const tree = this.router.parseUrl(current);
      const primaryPath = tree.root.children['primary']?.segments.map(s => s.path).join('/') || '';
      const currentTab = this.route.snapshot.queryParamMap.get('tab');
      const tab = tree.queryParams['tab'] || currentTab;

      if (tab) {
        tree.queryParams = {
          ...tree.queryParams,
          tab
        };
      } else if (
        primaryPath.startsWith('tenant/detail/') ||
        primaryPath.startsWith('tenants/detail/') ||
        primaryPath.startsWith('tenant/service-number-management/')
      ) {
        tree.queryParams = {
          ...tree.queryParams,
          tab: 'servicenumbers'
        };
      }

      return this.router.serializeUrl(tree);
    } catch {
      return current;
    }
  }
  // 获取选中的服务号
  getSelectedServiceNumbers(): any[] {
    return Array.from(this.selectedServiceNumberMap.values());
  }

  onItemChecked(id: string, item: any, checked: boolean): void {
    this.updateCheckedSet(id, checked, item);
    this.refreshCheckedStatus();
  }

  onAllChecked(checked: boolean): void {
    this.listOfCurrentPageData.forEach(item => {
      this.updateCheckedSet(item.id, checked, item);
    });
    this.refreshCheckedStatus();
  }
  updateCheckedSet(id: string, checked: boolean, item: any): void {
    if (checked) {
      const serviceNumber = item;
      if (serviceNumber) {
        this.selectedServiceNumberMap.set(id, {
          id: serviceNumber.id,
          name: serviceNumber.name
        });
      }
    } else {
      this.selectedServiceNumberMap.delete(id);
    }
  }
  refreshCheckedStatus(): void {
    const checkedIds = Array.from(this.selectedServiceNumberMap.keys());
    this.checked = this.listOfCurrentPageData.length > 0 && this.listOfCurrentPageData.every(({ id }) => checkedIds.includes(id));
    this.indeterminate = this.listOfCurrentPageData.some(({ id }) => checkedIds.includes(id)) && !this.checked;
    this.cdr.detectChanges();
  }
  onCurrentPageDataChange(data: readonly any[]): void {
    this.listOfCurrentPageData = data;
    this.refreshCheckedStatus();
  }

  // 限制条件：仅一般租户(Common)或官方租户(Official)，且服务号类型非 Official/Manage
  private isAllowedTenantType(): boolean {
    return this.tenantType === 'Common' || this.tenantType === 'Official';
  }
  private isNonOfficialNonManage(data: any): boolean {
    return data?.type !== 'Official' && data?.type !== 'Manage';
  }

  // 是否显示启用按钮
  shouldShowEnableButton(data: any): boolean {
    return data?.status !== 'Enable' && this.isAllowedTenantType() && this.isNonOfficialNonManage(data);
  }

  // 是否显示禁用按钮
  shouldShowDisableButton(data: any): boolean {
    return data?.status === 'Enable' && this.isAllowedTenantType() && this.isNonOfficialNonManage(data);
  }

  // 启用服务号
  enableServiceNumber(data: any): void {
    if (!data?.id) {
      return;
    }
    const url = `${ConstantUtil.TENANT_SERVICENUMBER_V1_ENABLE}/${data.id}`;
    this.loading = true;
    this.http
      .post(url, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success === true && (res.data === true || res.data === undefined)) {
          this.message.success(this.i18n.fanyi('servicenumber.enable-success'));
          this.loadData(false);
        } else {
          this.message.error(this.i18n.fanyi('tenant.employee.operation-failed'));
        }
      });
  }

  // 禁用服务号
  disableServiceNumber(data: any): void {
    if (!data?.id) {
      return;
    }
    const url = `${ConstantUtil.TENANT_SERVICENUMBER_V1_DISABLE}/${data.id}`;
    this.loading = true;
    this.http
      .post(url, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success === true && (res.data === true || res.data === undefined)) {
          this.message.success(this.i18n.fanyi('servicenumber.disable-success'));
          this.loadData(false);
        } else {
          this.message.error(this.i18n.fanyi('tenant.employee.operation-failed'));
        }
      });
  }
}
